/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */
import {
  Container,
  Heading,
  Grid,
  GridItem,
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Text,
  useColorMode,
  HStack,
  Button,
  IconButton,
} from "@chakra-ui/react";
import type { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { useState, useCallback } from "react";
import { FiSearch, FiChevronLeft, FiChevronRight } from "react-icons/fi";

import Loading from "lib/components/CenterLoading";
import Banner from "lib/components/home/Banner";
import CTASection from "lib/components/home/CTASection";
import IntercomHelp from "lib/components/IntercomHelp";
import type { NewsPostProps } from "lib/components/news/News";
import NewsCardPrimary from "lib/components/news/NewsCardPrimary";
import NewsCardSecondary from "lib/components/news/NewsCardSecondary";
import NewsHero from "lib/components/news/NewsHero";

const BASE_FIELDS = `fields=title,description,slug,content,date_created,date_updated,media,user_created.first_name,user_created.last_name,user_created.title,user_created.avatar,user_updated.first_name,user_updated.last_name,user_updated.title,user_updated.avatar,category&sort=-date_created`;
const ITEMS_PER_PAGE = 10;

interface APIProps {
  data: NewsPostProps[];
  totalCount: number;
  initialPage: number;
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  colorMode: string;
  onPageChange: (page: number) => void;
}

const Pagination = ({
  currentPage,
  totalPages,
  colorMode,
  onPageChange,
}: PaginationProps) => {
  const getButtonColor = (page: number) => {
    if (currentPage === page) return "white";
    return colorMode === "light"
      ? "text.light-secondary"
      : "text.dark-secondary";
  };

  const getButtonHoverBg = (page: number) => {
    if (currentPage === page) {
      return "brand.600";
    }
    return colorMode === "light" ? "neutral.100" : "neutral.800";
  };

  const pagesToShow = Array.from(
    { length: totalPages },
    (_, i) => i + 1
  ).filter(
    (page) =>
      page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1
  );

  return (
    <HStack justify="center" spacing={2} pt={10}>
      <IconButton
        aria-label="Previous page"
        icon={<FiChevronLeft />}
        onClick={() => onPageChange(currentPage - 1)}
        isDisabled={currentPage === 1}
        variant="outline"
        borderRadius="xl"
        borderColor={colorMode === "light" ? "neutral.100" : "neutral.800"}
        _hover={{
          bg: colorMode === "light" ? "neutral.100" : "neutral.800",
        }}
      />
      <HStack spacing={1}>
        {pagesToShow.map((page, index, arr) => {
          const showEllipsisBefore = index > 0 && arr[index - 1] !== page - 1;
          return (
            <HStack key={page} spacing={1}>
              {showEllipsisBefore && (
                <Text
                  color={
                    colorMode === "light"
                      ? "text.light-muted"
                      : "text.dark-muted"
                  }
                  px={2}
                >
                  ...
                </Text>
              )}
              <Button
                onClick={() => onPageChange(page)}
                variant={currentPage === page ? "solid" : "outline"}
                bg={currentPage === page ? "brand.500" : "transparent"}
                color={getButtonColor(page)}
                borderColor={
                  colorMode === "light" ? "neutral.100" : "neutral.800"
                }
                borderRadius="xl"
                minW="40px"
                _hover={{
                  bg: getButtonHoverBg(page),
                }}
              >
                {page}
              </Button>
            </HStack>
          );
        })}
      </HStack>
      <IconButton
        aria-label="Next page"
        icon={<FiChevronRight />}
        onClick={() => onPageChange(currentPage + 1)}
        isDisabled={currentPage === totalPages}
        variant="outline"
        borderRadius="xl"
        borderColor={colorMode === "light" ? "neutral.100" : "neutral.800"}
        _hover={{
          bg: colorMode === "light" ? "neutral.100" : "neutral.800",
        }}
      />
    </HStack>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getStaticProps: GetStaticProps = async () => {
  const baseUrl = `${process.env.NEXT_PUBLIC_DIRECTUS_URL}/items/blog`;
  const res = await fetch(
    `${baseUrl}?${BASE_FIELDS}&limit=${ITEMS_PER_PAGE}&offset=0&meta=filter_count`
  );
  const posts = await res.json();

  return {
    props: {
      data: posts.data || [],
      totalCount: posts.meta?.filter_count || 0,
      initialPage: 1,
    },
    revalidate: 60,
  };
};

interface NewsResultsProps {
  loading: boolean;
  searchDisplayActive: boolean;
  dta: NewsPostProps[];
  colorMode: string;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}

const updatePageInUrl = (page: number) => {
  const url = new URL(window.location.href);
  if (page === 1) {
    url.searchParams.delete("page");
  } else {
    url.searchParams.set("page", page.toString());
  }
  window.history.pushState({}, "", url.toString());
};

const NewsResults = ({
  loading,
  searchDisplayActive,
  dta,
  colorMode,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
}: NewsResultsProps) => {
  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Grid
        w="100%"
        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }}
        gap={8}
      >
        {!searchDisplayActive && dta[0] ? (
          <GridItem colSpan={{ base: 1, md: 2 }}>
            <NewsCardPrimary
              title={dta[0].title}
              description={dta[0].description}
              date_created={dta[0].date_created}
              media={dta[0].media}
              slug={dta[0].slug}
              user_created={dta[0].user_created}
              content={dta[0].content}
              date_updated={dta[0].date_updated}
              user_updated={dta[0].user_updated}
              category={dta[0].category}
            />
          </GridItem>
        ) : null}
        {!searchDisplayActive && dta.length > 1 ? (
          <GridItem colSpan={{ base: 1, md: 2 }} py={4}>
            <Heading
              fontSize={{ base: "xl", md: "2xl" }}
              fontWeight={700}
              color={
                colorMode === "light"
                  ? "text.light-primary"
                  : "text.dark-primary"
              }
              textAlign="center"
            >
              <Text
                as="span"
                bgGradient="linear(to-r, brand.500, brand.700)"
                bgClip="text"
              >
                More
              </Text>{" "}
              Articles
            </Heading>
          </GridItem>
        ) : null}
        {dta.map((blogData, i) => {
          if (i === 0 && !searchDisplayActive) return null;
          return (
            <GridItem key={i}>
              <NewsCardSecondary
                title={blogData.title}
                description={blogData.description}
                date_created={blogData.date_created}
                media={blogData.media}
                slug={blogData.slug}
                user_created={blogData.user_created}
                content={blogData.content}
                date_updated={blogData.date_updated}
                user_updated={blogData.user_updated}
                category={blogData.category}
              />
            </GridItem>
          );
        })}
      </Grid>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          colorMode={colorMode}
          onPageChange={onPageChange}
        />
      )}

      {/* Page info */}
      {totalItems > 0 && (
        <Text
          textAlign="center"
          color={colorMode === "light" ? "text.light-muted" : "text.dark-muted"}
          fontSize="sm"
          pt={4}
        >
          Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} -{" "}
          {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} of {totalItems}{" "}
          articles
        </Text>
      )}
    </>
  );
};

const Blog = ({ data, totalCount, initialPage }: APIProps) => {
  const { colorMode } = useColorMode();
  const [dta, setDta] = useState(data);
  const [searchDisplayActive, setSearchDisplayActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalItems, setTotalItems] = useState(totalCount);
  const [searchQuery, setSearchQuery] = useState("");

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  const fetchPage = useCallback(async (page: number, searchText = "") => {
    setLoading(true);
    const offset = (page - 1) * ITEMS_PER_PAGE;
    const baseUrl = `${process.env.NEXT_PUBLIC_DIRECTUS_URL}/items/blog`;

    let url = `${baseUrl}?${BASE_FIELDS}&limit=${ITEMS_PER_PAGE}&offset=${offset}&meta=filter_count`;

    if (searchText) {
      url += `&filter[_or][0][title][_contains]=${encodeURIComponent(
        searchText
      )}&filter[_or][1][description][_contains]=${encodeURIComponent(
        searchText
      )}`;
    }

    const res = await fetch(url);
    const posts = await res.json();

    setDta(posts.data || []);
    setTotalItems(posts.meta?.filter_count || 0);
    setCurrentPage(page);
    setLoading(false);
  }, []);

  const search = async (text: string) => {
    setSearchQuery(text);
    setSearchDisplayActive(text !== "");
    await fetchPage(1, text);
  };

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    fetchPage(page, searchQuery);
    updatePageInUrl(page);
  };

  return (
    <>
      <NextSeo
        title="Adiona Alert News & Guides"
        description="Expert guides on backcountry weather safety, satellite communicator tips, and outdoor emergency preparedness. Covering Garmin inReach, ZOLEO, and Bivy Stick users across the US and Canada."
      />
      <Banner />
      <NewsHero />

      {/* Main Content */}
      <Box py={{ base: 8, md: 12 }} position="relative">
        {/* Background pattern */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.5}
          bgImage={
            colorMode === "light"
              ? "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.03) 1px, transparent 0)"
              : "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0)"
          }
          bgSize="40px 40px"
        />

        <Container maxW="5xl" position="relative">
          {/* Search Bar */}
          <Box pb={10} maxW="md" mx="auto">
            <InputGroup size="lg">
              <InputLeftElement pointerEvents="none">
                <Icon
                  as={FiSearch}
                  color={
                    colorMode === "light"
                      ? "text.light-muted"
                      : "text.dark-muted"
                  }
                />
              </InputLeftElement>
              <Input
                aria-label="Search articles"
                name="search"
                placeholder="Search articles..."
                bg={colorMode === "light" ? "white" : "neutral.800"}
                border="1px solid"
                borderColor={
                  colorMode === "light" ? "neutral.200" : "neutral.800"
                }
                borderRadius="xl"
                _placeholder={{
                  color:
                    colorMode === "light"
                      ? "text.light-muted"
                      : "text.dark-muted",
                }}
                _focus={{
                  borderColor: "brand.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                }}
                onChange={(e) => search(e.target.value)}
              />
            </InputGroup>
          </Box>

          <NewsResults
            loading={loading}
            searchDisplayActive={searchDisplayActive}
            dta={dta}
            colorMode={colorMode}
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={goToPage}
          />
        </Container>
      </Box>
      <CTASection />
      <IntercomHelp />
    </>
  );
};

export default Blog;
